import React, { useState, useEffect, useRef, useCallback } from "react";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import { useNavigate } from "react-router-dom";
import { GrFormPrevious, GrFormNext } from "react-icons/gr";
import { Product } from "../types";
import ProductCard from "../components/ProductCard";
import { spacing, green, black, white, fontSize, fontWeight } from "../theme";
import navConfig from "../nav.config";

const publicBase = process.env.PUBLIC_URL || "";

const ENERGY_CALCULATOR_PATH = "/energi-kalkylator";
const INTERVAL_CALCULATOR_PATH = "/intervall-kalkylator";

type HeroSlide = {
  src: string;
  alt: string;
  eyebrow: string;
  headline: string;
  tagline: string;
  cta: string;
  path: string;
};

const ENERGY_COPY = {
  eyebrow: "Trirebels energikalkylator",
  headline: "Bygg din egen raceplan",
  tagline:
    "Berätta vilken sport, distans och tid du siktar på – vi räknar ut din kolhydratbudget och föreslår energipaket och energiplan.",
  cta: "Öppna energikalkylatorn",
  path: ENERGY_CALCULATOR_PATH,
} as const;

const INTERVAL_COPY = {
  eyebrow: "Trirebels intervallkalkylator",
  headline: "Bygg ditt intervallpass",
  tagline:
    "Välj cykel eller löpning och vad du vill träna – få ett konkret pass med watt eller tempo i min/km.",
  cta: "Öppna intervallkalkylatorn",
  path: INTERVAL_CALCULATOR_PATH,
} as const;

/** Växlar energi / intervall så samma verktyg inte kommer fyra i rad. */
const HERO_SLIDES: HeroSlide[] = [
  {
    src: `${publicBase}/hero/energy-calc-triathlon.png`,
    alt: "Triathlon – energi, vätska och näring vid simning, cykling och löpning",
    ...ENERGY_COPY,
  },
  {
    src: `${publicBase}/hero/interval-calc-cycling.jpg`,
    alt: "Cykling – intervallpass styrt med FTP och watt",
    ...INTERVAL_COPY,
  },
  {
    src: `${publicBase}/hero/energy-calc-running.png`,
    alt: "Löpning – planera energi och vätska inför loppet",
    ...ENERGY_COPY,
  },
  {
    src: `${publicBase}/hero/interval-calc-running.jpg`,
    alt: "Löpning – intervallpass styrt med tempo och tröskelfart",
    ...INTERVAL_COPY,
  },
  {
    src: `${publicBase}/hero/energy-calc-cycling.png`,
    alt: "Cykling – planera energi och vätska inför loppet",
    ...ENERGY_COPY,
  },
  {
    src: `${publicBase}/hero/energy-calc-app.png`,
    alt: "Trirebels energikalkylator – bygg din egen raceplan",
    ...ENERGY_COPY,
  },
];

const StartPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [heroSlide, setHeroSlide] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/api/products/featured")
      .then((response) => response.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  }, []);

  const heroGo = useCallback(
    (delta: number) => {
      setHeroSlide((s) => (s + delta + HERO_SLIDES.length) % HERO_SLIDES.length);
    },
    []
  );

  useEffect(() => {
    if (heroPaused) return;
    const id = window.setInterval(() => {
      setHeroSlide((s) => (s + 1) % HERO_SLIDES.length);
    }, 5500);
    return () => window.clearInterval(id);
  }, [heroPaused]);

  return (
    <Page className="start-page">
      <HeroCarousel
        className="hero-carousel"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
        onFocus={() => setHeroPaused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setHeroPaused(false);
        }}
      >
        <HeroSlideStack>
          {HERO_SLIDES.map((slide, i) => (
            <HeroSlideFrame
              key={slide.src}
              css={{ opacity: i === heroSlide ? 1 : 0 }}
            >
              <HeroSlideImage
                src={slide.src}
                alt={slide.alt}
                loading={i === 0 ? "eager" : "lazy"}
              />
            </HeroSlideFrame>
          ))}
        </HeroSlideStack>
        <HeroGradient />
        <HeroBottom className="hero-carousel-bottom">
          <HeroCopy>
            <HeroEyebrow>{HERO_SLIDES[heroSlide].eyebrow}</HeroEyebrow>
            <HeroHeadline className="hero-carousel-title">
              {HERO_SLIDES[heroSlide].headline}
            </HeroHeadline>
            <HeroTagline>{HERO_SLIDES[heroSlide].tagline}</HeroTagline>
            <HeroCta
              type="button"
              onClick={() => navigate(HERO_SLIDES[heroSlide].path)}
            >
              {HERO_SLIDES[heroSlide].cta}
            </HeroCta>
          </HeroCopy>
          <HeroDots role="tablist" aria-label="Välj bild" className="hero-carousel-dots">
            {HERO_SLIDES.map((_, i) => (
              <HeroDot
                key={i}
                type="button"
                role="tab"
                aria-selected={i === heroSlide}
                aria-label={`Bild ${i + 1}`}
                css={{
                  width: i === heroSlide ? "22px" : "8px",
                  backgroundColor:
                    i === heroSlide ? white : "rgba(255,255,255,0.45)",
                }}
                onClick={() => setHeroSlide(i)}
              />
            ))}
          </HeroDots>
        </HeroBottom>
        <HeroNavPrev
          type="button"
          className="hero-nav-prev"
          aria-label="Föregående bild"
          onClick={() => heroGo(-1)}
        >
          <GrFormPrevious size={22} />
        </HeroNavPrev>
        <HeroNavNext
          type="button"
          className="hero-nav-next"
          aria-label="Nästa bild"
          onClick={() => heroGo(1)}
        >
          <GrFormNext size={22} />
        </HeroNavNext>
      </HeroCarousel>

      <Section>
        <SectionWrapper>
          <Divider />
          <SectionTitle className="section-title">Utvalda produkter</SectionTitle>
          <Divider />
        </SectionWrapper>
        <ProductGrid className="start-product-grid">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </ProductGrid>
      </Section>

      <Section ref={categoriesRef} id="kategorier">
        <SectionTitle className="section-title">Shopa efter kategori</SectionTitle>
        <CategoryGrid className="category-grid">
          {navConfig.categories.map((cat) => (
            <CategoryCard
              key={cat.slug}
              onClick={() => navigate(`/kategorier/${cat.slug}`)}
            >
              <CategoryName>{cat.label}</CategoryName>
              <CategoryArrow>→</CategoryArrow>
            </CategoryCard>
          ))}
        </CategoryGrid>
      </Section>
    </Page>
  );
};

const Page = styled.main({
  width: "100%",
  maxWidth: "1200px",
  margin: { x: "auto", y: 0 },
  padding: { x: spacing.m, y: spacing.xl },
});

const HeroCarousel = styled.section({
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  margin: { bottom: spacing.xxxl },
  backgroundColor: "#0a0a0a",
  aspectRatio: "21 / 9",
  ...media({ maxWidth: "768px" }, {
    aspectRatio: "4 / 3",
    minHeight: "220px",
  }),
});

const HeroSlideStack = styled.div({
  position: "absolute",
  inset: 0,
});

const HeroSlideFrame = styled.div({
  position: "absolute",
  inset: 0,
  transitionProperty: "opacity",
  transitionDuration: "0.7s",
  transitionTimingFunction: "ease-in-out",
  pointerEvents: "none",
});

const HeroSlideImage = styled.img({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
});

const HeroGradient = styled.div({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: "55%",
  backgroundImage: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)",
  pointerEvents: "none",
  zIndex: 1,
});

const HeroBottom = styled.div({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: { top: spacing.xl, bottom: spacing.m, left: spacing.xl, right: spacing.xl },
  gap: spacing.m,
  pointerEvents: "auto",
  ...media({ maxWidth: "768px" }, {
    padding: { top: spacing.m, bottom: spacing.s, left: spacing.m, right: spacing.m },
    gap: spacing.s,
  }),
});

const HeroCopy = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  maxWidth: "100%",
});

const HeroEyebrow = styled.span({
  display: "block",
  color: "rgba(255,255,255,0.85)",
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  margin: { bottom: spacing.xs },
  maxWidth: "520px",
});

const HeroHeadline = styled.h1({
  color: white,
  fontSize: fontSize.xxl,
  fontWeight: fontWeight.heavy,
  margin: { top: 0, right: 0, bottom: spacing.xs, left: 0 },
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  maxWidth: "520px",
  ...media({ minWidth: "769px" }, {
    fontSize: fontSize.hero,
  }),
});

const HeroTagline = styled.p({
  color: "rgba(255,255,255,0.9)",
  fontSize: fontSize.m,
  lineHeight: 1.45,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  maxWidth: "440px",
  textAlign: "center",
});

const HeroCta = styled.button({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: spacing.m,
  padding: { x: spacing.xl, y: spacing.m },
  backgroundColor: green,
  color: white,
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  fontFamily: "inherit",
  borderRadius: "8px",
  border: { xy: { style: "solid", color: green, width: "2px" } },
  cursor: "pointer",
  transitionProperty: "background-color, color, transform",
  transitionDuration: "0.2s",
  ":hover": {
    backgroundColor: white,
    color: green,
  },
  ":active": {
    transform: "scale(0.98)",
  },
});

const HeroNavPrev = styled.button({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 3,
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  border: { xy: { style: "none", width: 0, color: "transparent" } },
  backgroundColor: "rgba(255,255,255,0.92)",
  color: black,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
  transitionProperty: "background-color, transform",
  transitionDuration: "0.2s",
  left: spacing.m,
  ":hover": {
    backgroundColor: white,
  },
  ":active": {
    transform: "translateY(-50%) scale(0.96)",
  },
});

const HeroNavNext = styled.button({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 3,
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  border: { xy: { style: "none", width: 0, color: "transparent" } },
  backgroundColor: "rgba(255,255,255,0.92)",
  color: black,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
  transitionProperty: "background-color, transform",
  transitionDuration: "0.2s",
  right: spacing.m,
  ":hover": {
    backgroundColor: white,
  },
  ":active": {
    transform: "translateY(-50%) scale(0.96)",
  },
});

const HeroDots = styled.div({
  display: "flex",
  gap: spacing.xs,
  alignItems: "center",
});

const HeroDot = styled.button({
  height: "8px",
  borderRadius: "4px",
  border: { xy: { style: "none", width: 0, color: "transparent" } },
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  cursor: "pointer",
  transitionProperty: "width, background-color",
  transitionDuration: "0.25s",
  ":hover": {
    backgroundColor: "rgba(255,255,255,0.7)",
  },
});

const Section = styled.section({
  marginBottom: spacing.xxxl,
});

const SectionWrapper = styled.div({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: spacing.m,
  margin: { bottom: spacing.xl },
  flexWrap: "wrap",
});

const Divider = styled.div({
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: "48px",
  maxWidth: "200px",
  height: "1px",
  backgroundColor: black,
});

const SectionTitle = styled.h2({
  fontSize: fontSize.xxl,
  fontWeight: fontWeight.bold,
  color: black,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  whiteSpace: "nowrap",
});

const ProductGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: spacing.l,
});

const CategoryGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
  gap: spacing.m,
  marginTop: spacing.m,
});

const CategoryCard = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: { x: spacing.xl, y: spacing.l },
  backgroundColor: white,
  border: { xy: { style: "solid", color: "#e5e5e5", width: "1px" } },
  borderRadius: "8px",
  cursor: "pointer",
  transitionProperty: "border-color, box-shadow, transform",
  transitionDuration: "0.2s",
  transitionTimingFunction: "ease",
  ":hover": {
    border: { xy: { style: "solid", color: green, width: "1px" } },
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transform: "translateY(-2px)",
  },
});

const CategoryName = styled.span({
  fontSize: fontSize.ml,
  fontWeight: fontWeight.bold,
  color: black,
});

const CategoryArrow = styled.span({
  fontSize: fontSize.xl,
  color: green,
  fontWeight: fontWeight.bold,
});

export default StartPage;
